import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import chatService from "../services/chatService";
import OrgThemeContext from "../context/OrgThemeContext";
import UserAvatar from "../components/UserAvatar";
import {
  Trophy,
  Star,
  ArrowLeft,
  Mail,
  MapPin,
  Briefcase,
  Award,
  ChevronDown,
} from "lucide-react";
import { EnhancedRecognitionCard, NominationCard } from "../components/CoreFeed";
import useClickOutside from "../hooks/useClickOutside";
import { useTranslation } from "../i18n";
import BannerMedia from "../components/BannerMedia";

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const orgTheme = useContext(OrgThemeContext);
  const { t } = useTranslation("profile");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("received");
  const [recognitions, setRecognitions] = useState([]);
  const [loadingRecognitions, setLoadingRecognitions] = useState(false);
  const [nominations, setNominations] = useState([]);
  const [loadingNominations, setLoadingNominations] = useState(false);
  const [tabMenuOpen, setTabMenuOpen] = useState(false);
  const tabMenuRef = useRef(null);

  useClickOutside(tabMenuRef, tabMenuOpen, () => setTabMenuOpen(false));
  
  const [stats, setStats] = useState({
    recognitionsReceived: 0,
    recognitionsGiven: 0,
    pointsReceived: 0,
    pointsGiven: 0,
    nominationsReceived: 0,
    nominationsGiven: 0,
    nominationPointsReceived: 0,
  });

  // Determine the actual user ID for this profile
  const actualUserId = id === "me" ? currentUser?.id : id;
  const isOwnProfile = id === "me" || (currentUser && actualUserId === currentUser.id);

  // Compute stats from recognitions
  const computeStats = useCallback((recs, userId) => {
    const isRecipient = (r) =>
      r.recipient?.id === userId ||
      (r.co_recipients || []).some((cr) => cr.id === userId);
    const received = recs.filter(isRecipient);
    const given = recs.filter((r) => r.sender?.id === userId && !isRecipient(r));
    return {
      recognitionsReceived: received.length,
      recognitionsGiven: given.length,
      pointsReceived: received
        .filter((r) => r.points_status === "approved")
        .reduce((sum, r) => sum + (r.points_amount || 0), 0),
      pointsGiven: given
        .filter((r) => r.points_status === "approved")
        .reduce((sum, r) => sum + (r.points_amount || 0), 0),
    };
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) {
          setError(t("errors.auth_required"));
          setLoading(false);
          return;
        }

        // Handle /profile/me case
        if (id === "me") {
          if (currentUser) {
            const processedUser = {
              id: currentUser.id,
              name: currentUser.full_name || currentUser.name || t("fallbacks.unknown_user"),
              email: currentUser.email || "Not specified",
              avatar: currentUser.avatar_url || null,
              role: currentUser.role || t("fallbacks.team_member"),
              department: currentUser.department || "Not specified",
              location: currentUser.location || "Not specified",
              jobTitle: currentUser.job_title || null,
            };
            setUser(processedUser);
            window.dispatchEvent(new CustomEvent('profileViewed', {
              detail: { name: processedUser.name, isOwnProfile: true }
            }));
            setLoading(false);
            return;
          } else {
            // Wait for currentUser to load
            setLoading(false);
            return;
          }
        }

        const apiUrl = process.env.REACT_APP_API_URL || "";
        const response = await fetch(`${apiUrl}/api/v1/users/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(t("errors.auth_failed"));
          } else if (response.status === 403) {
            throw new Error(
              t("errors.access_denied")
            );
          } else if (response.status === 404) {
            throw new Error(
              t("errors.not_found")
            );
          } else {
            throw new Error(
              t("errors.server", { status: response.status })
            );
          }
        }

        const data = await response.json();

        const processedUser = {
          id: data.id,
          name: data.full_name || data.name || t("fallbacks.unknown_user"),
          email: data.email || "Not specified",
          avatar: data.avatar_url || null,
          role: data.role || t("fallbacks.team_member"),
          department: data.department || "Not specified",
          location: data.location || "Not specified",
          jobTitle: data.job_title || null,
        };

        setUser(processedUser);
        window.dispatchEvent(new CustomEvent('profileViewed', {
          detail: { name: processedUser.name, isOwnProfile: false }
        }));
      } catch (err) {
        console.error("Error fetching user:", err);
        setError(err.message || t("errors.load_failed"));
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, currentUser]);

  // Fetch recognitions when user is loaded
  useEffect(() => {
    if (!user || !actualUserId) return;

    const fetchRecognitions = async () => {
      try {
        setLoadingRecognitions(true);
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "";

        let allRecognitions = [];

        if (isOwnProfile) {
          // Use /recognitions/me for current user
          const response = await fetch(`${apiUrl}/api/v1/recognitions/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            allRecognitions = Array.isArray(data) ? data : data.data || [];
          }
        } else {
          // For other users, use /recognitions/feed and filter client-side
          const response = await fetch(
            `${apiUrl}/api/v1/recognitions/feed?skip=0&limit=500`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (response.ok) {
            const data = await response.json();
            const feedData = Array.isArray(data) ? data : data.data || [];
            allRecognitions = feedData.filter(
              (rec) =>
                rec.sender?.id === actualUserId ||
                rec.recipient?.id === actualUserId ||
                (rec.co_recipients || []).some((cr) => cr.id === actualUserId)
            );
          }
        }

        setRecognitions(allRecognitions);
        const recStats = computeStats(allRecognitions, actualUserId);
        setStats((prev) => ({ ...prev, ...recStats }));
      } catch (err) {
        console.error("Error fetching recognitions:", err);
        setRecognitions([]);
      } finally {
        setLoadingRecognitions(false);
      }
    };

    fetchRecognitions();
  }, [user, actualUserId, isOwnProfile, computeStats]);

  // Fetch nominations when user is loaded
  useEffect(() => {
    if (!user || !actualUserId) return;

    const fetchNominations = async () => {
      try {
        setLoadingNominations(true);
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "";

        const response = await fetch(
          `${apiUrl}/api/v1/nominations/feed?skip=0&limit=500`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const feedData = Array.isArray(data) ? data : data.data || [];
          const userNominations = feedData.filter(
            (nom) =>
              nom.sender?.id === actualUserId ||
              nom.recipient?.id === actualUserId
          );
          setNominations(userNominations);

          // Compute nomination stats
          const received = userNominations.filter(
            (n) => n.recipient?.id === actualUserId
          );
          const given = userNominations.filter(
            (n) => n.sender?.id === actualUserId
          );
          const nominationPointsReceived = received.reduce(
            (sum, n) => sum + (n.points_awarded_recipient || 0),
            0
          );

          setStats((prev) => ({
            ...prev,
            nominationsReceived: received.length,
            nominationsGiven: given.length,
            nominationPointsReceived,
          }));
        }
      } catch (err) {
        console.error("Error fetching nominations:", err);
        setNominations([]);
      } finally {
        setLoadingNominations(false);
      }
    };

    fetchNominations();
  }, [user, actualUserId]);

  // Filter recognitions by tab — include cards where user is a co-recipient
  const isRecipientOf = (rec) =>
    rec.recipient?.id === actualUserId ||
    (rec.co_recipients || []).some((cr) => cr.id === actualUserId);
  const receivedRecognitions = recognitions.filter(isRecipientOf);
  const givenRecognitions = recognitions.filter(
    (rec) => rec.sender?.id === actualUserId && !isRecipientOf(rec)
  );

  // Filter nominations by tab
  const receivedNominations = nominations.filter(
    (nom) => nom.recipient?.id === actualUserId
  );
  const givenNominations = nominations.filter(
    (nom) => nom.sender?.id === actualUserId
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600 mb-3"></div>
          <p className="text-gray-600">{t("loading.profile")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center p-4">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-lg mb-4">
            {t("errors.title")}
          </div>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-x-3">
            <button
              className="px-4 py-2 org-bg-primary org-btn text-white rounded-full transition-all"
              onClick={() => window.location.reload()}
            >
              {t("actions.try_again")}
            </button>
            <button
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition"
              onClick={() => navigate(-1)}
            >
              {t("actions.go_back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center p-4">
        <div className="text-center max-w-md">
          <div className="text-gray-500 text-lg mb-4">{t("not_found.title")}</div>
          <p className="text-gray-600 mb-6">
            {t("not_found.body")}
          </p>
          <button
            className="px-4 py-2 org-bg-primary org-btn text-white rounded-full transition-all"
            onClick={() => navigate("/")}
          >
            {t("actions.return_home")}
          </button>
        </div>
      </div>
    );
  }

  const profileTabs = [
    {
      key: "received",
      label: t("stats.recognitions_received"),
      Icon: Trophy,
      count: stats.recognitionsReceived,
      activeClass: "org-gradient text-white shadow-md",
    },
    {
      key: "given",
      label: t("stats.recognitions_given"),
      Icon: Star,
      count: stats.recognitionsGiven,
      activeClass: "org-gradient text-white shadow-md",
    },
    {
      key: "nominations_received",
      label: t("stats.nominations_received"),
      Icon: Award,
      count: stats.nominationsReceived,
      activeClass: "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md",
    },
    {
      key: "nominations_given",
      label: t("stats.nominations_given"),
      Icon: Award,
      count: stats.nominationsGiven,
      activeClass: "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md",
    },
  ];
  const activeTabDef = profileTabs.find((tab) => tab.key === activeTab) || profileTabs[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto pb-8 pt-3 sm:pt-8 px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-purple-600 transition mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span>{t("common:actions.back")}</span>
        </button>

        {/* Profile Header */}
        <div className="relative mb-8">
          {/* Cover Photo */}
          <div className="h-48 rounded-2xl overflow-hidden relative">
            <BannerMedia
              imageUrl={orgTheme.bannerImageUrl}
              videoUrl={orgTheme.bannerVideoUrl}
              position={orgTheme.bannerPosition}
              zoom={orgTheme.bannerZoom}
              alt={t("banner_alt")}
              className="w-full h-full object-cover"
              fallback={
                <div
                  className="w-full h-full"
                  style={{ background: `linear-gradient(to right, ${orgTheme.primaryColor}, ${orgTheme.secondaryColor})` }}
                >
                  <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1IiBoZWlnaHQ9IjUiPgo8cmVjdCB3aWR0aD0iNSIgaGVpZ2h0PSI1IiBmaWxsPSIjZmZmIj48L3JlY3Q+CjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNjY2MiPjwvcmVjdD4KPC9zdmc+')]"></div>
                </div>
              }
            />
          </div>

          {/* Profile Info Section */}
          <div className="absolute -bottom-16 w-full px-4">
            <div className="flex flex-col md:flex-row items-center md:items-end bg-white rounded-2xl p-4 md:p-6 shadow-md">
              {/* User Avatar */}
              <div className="relative -mt-20 mb-4 md:mb-0 md:mr-6">
                <UserAvatar
                  src={user.avatar}
                  name={user.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  textClassName="text-4xl"
                />

              </div>

              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl font-bold text-gray-900">
                  {user.name}
                </h1>
                <div className="mt-1 mb-2">
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {user.jobTitle || user.role}
                  </span>
                </div>

                {/* User Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  {user.department && user.department !== "Not specified" && (
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 mr-1 text-gray-500" />
                      <span>{user.department}</span>
                    </div>
                  )}

                  {user.location && user.location !== "Not specified" && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1 text-gray-500" />
                      <span>{user.location}</span>
                    </div>
                  )}

                  {user.email && user.email !== "Not specified" && (
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-1 text-gray-500" />
                      <span>{user.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Message (chat is a per-org switch; never offered on your own profile) */}
              {currentUser?.organization_customization?.chat_enabled && currentUser?.id !== id && (
                <button
                  onClick={() =>
                    chatService
                      .openDirect(id)
                      .then((conv) => navigate(`/chat/${conv.id}`))
                      .catch(() => {})
                  }
                  className="mt-4 md:mt-0 md:ml-4 px-4 py-2 rounded-full org-bg-primary org-btn text-white text-sm flex items-center gap-2 flex-shrink-0"
                >
                  <Mail className="w-4 h-4" />
                  {t("message_button")}
                </button>
              )}

            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mt-20 mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow-md p-4 flex flex-col items-center text-center">
            <div className="p-2 rounded-full bg-purple-100 mb-2">
              <Trophy className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-purple-600">{stats.recognitionsReceived}</div>
            <div className="text-sm text-gray-500 mt-1">{t("stats.recognitions_received")}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-4 flex flex-col items-center text-center">
            <div className="p-2 rounded-full bg-blue-100 mb-2">
              <Star className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-blue-600">{stats.recognitionsGiven}</div>
            <div className="text-sm text-gray-500 mt-1">{t("stats.recognitions_given")}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-4 flex flex-col items-center text-center">
            <div className="p-2 rounded-full bg-amber-100 mb-2">
              <Award className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-3xl font-bold text-amber-600">{stats.nominationsReceived}</div>
            <div className="text-sm text-gray-500 mt-1">{t("stats.nominations_received")}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-4 flex flex-col items-center text-center">
            <div className="p-2 rounded-full bg-amber-100 mb-2">
              <Award className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-3xl font-bold text-amber-600">{stats.nominationsGiven}</div>
            <div className="text-sm text-gray-500 mt-1">{t("stats.nominations_given")}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          {/* Mobile: dropdown tab selector;*/}
          <div className="relative sm:hidden mb-3" ref={tabMenuRef}>
            <button
              type="button"
              aria-expanded={tabMenuOpen}
              aria-haspopup="listbox"
              onClick={() => setTabMenuOpen((v) => !v)}
              className={`w-full flex items-center justify-between gap-2 rounded-2xl px-4 py-3 text-sm font-medium ${activeTabDef.activeClass}`}
            >
              <span className="inline-flex items-center gap-2">
                <activeTabDef.Icon className="w-4 h-4" />
                {t("tabs.with_count", { label: activeTabDef.label, count: activeTabDef.count })}
              </span>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${
                  tabMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {tabMenuOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-2 z-20 space-y-1">
                {profileTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab.key);
                      setTabMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${
                      activeTab === tab.key
                        ? tab.activeClass
                        : "text-gray-700 hover:bg-gray-100 active:bg-gray-100"
                    }`}
                  >
                    <tab.Icon className="w-4 h-4" />
                    {t("tabs.with_count", { label: tab.label, count: tab.count })}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="hidden sm:flex overflow-x-auto bg-white rounded-t-2xl shadow-sm p-2 border-b border-gray-200">
            {profileTabs.map((tab) => (
              <button
                key={tab.key}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center ${
                  activeTab === tab.key ? tab.activeClass : "text-gray-600 hover:bg-gray-100"
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                <tab.Icon className="w-4 h-4 mr-2" />
                {t("tabs.with_count", { label: tab.label, count: tab.count })}
              </button>
            ))}
          </div>

          <div className="bg-transparent sm:bg-white sm:rounded-b-2xl sm:shadow-md p-0 pt-3 sm:p-6">
            {/* Recognitions Received Tab */}
            {activeTab === "received" && (
              <div>
                <div className="hidden sm:flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t("stats.recognitions_received")}
                  </h2>
                  <span className="text-sm text-gray-500">
                    {t("counts.recognitions", { count: receivedRecognitions.length })}
                  </span>
                </div>

                {loadingRecognitions ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">{t("loading.recognitions")}</p>
                  </div>
                ) : receivedRecognitions.length > 0 ? (
                  <div className="space-y-5">
                    {receivedRecognitions.map((rec) => <EnhancedRecognitionCard key={rec.id} data={rec} />)}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-2">
                      {t("empty.recognitions_received")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Recognitions Given Tab */}
            {activeTab === "given" && (
              <div>
                <div className="hidden sm:flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t("stats.recognitions_given")}
                  </h2>
                  <span className="text-sm text-gray-500">
                    {t("counts.recognitions", { count: givenRecognitions.length })}
                  </span>
                </div>

                {loadingRecognitions ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">{t("loading.recognitions")}</p>
                  </div>
                ) : givenRecognitions.length > 0 ? (
                  <div className="space-y-5">
                    {givenRecognitions.map((rec) => <EnhancedRecognitionCard key={rec.id} data={rec} />)}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <Star className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-2">
                      {t("empty.recognitions_given")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Nominations Received Tab */}
            {activeTab === "nominations_received" && (
              <div>
                <div className="hidden sm:flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t("stats.nominations_received")}
                  </h2>
                  <span className="text-sm text-gray-500">
                    {t("counts.nominations", { count: receivedNominations.length })}
                  </span>
                </div>

                {loadingNominations ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">{t("loading.nominations")}</p>
                  </div>
                ) : receivedNominations.length > 0 ? (
                  <div className="space-y-5">
                    {receivedNominations.map((nom) => <NominationCard key={nom.id} data={nom} />)}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <Award className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-2">
                      {t("empty.nominations_received")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Nominations Given Tab */}
            {activeTab === "nominations_given" && (
              <div>
                <div className="hidden sm:flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t("stats.nominations_given")}
                  </h2>
                  <span className="text-sm text-gray-500">
                    {t("counts.nominations", { count: givenNominations.length })}
                  </span>
                </div>

                {loadingNominations ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">{t("loading.nominations")}</p>
                  </div>
                ) : givenNominations.length > 0 ? (
                  <div className="space-y-5">
                    {givenNominations.map((nom) => <NominationCard key={nom.id} data={nom} />)}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <Award className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-2">
                      {t("empty.nominations_given")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
